import React, { useState } from "react";
import {
  CloseIcon,
  ImageIcon,
  InstagramIcon,
  LinkIcon,
  PinterestIcon,
  PlayIcon,
  TikTokIcon,
  YouTubeIcon,
} from "@/components/icons";
import type {
  IdeaResource,
  IdeaResourceEmbed,
  IdeaResourceEmbedProvider,
  IdeaResourceLocalMediaItem,
} from "@/domains/Ideas/types";
import { ResourcePill } from "./ResourcePill";
import { InlineMediaPlayer } from "./InlineMediaPlayer";

type Variant = "portal" | "admin" | "dense";

interface ResourceEmbedCardProps {
  resource: IdeaResource;
  variant?: Variant;
  onRemove?: () => void;
  isRemoving?: boolean;
}

const PROVIDER_BADGES: Record<
  IdeaResourceEmbedProvider,
  { label: string; Icon: React.FC<{ className?: string }>; brandClass: string }
> = {
  instagram: { label: "Instagram", Icon: InstagramIcon, brandClass: "text-pink-600" },
  pinterest: { label: "Pinterest", Icon: PinterestIcon, brandClass: "text-red-600" },
  tiktok: { label: "TikTok", Icon: TikTokIcon, brandClass: "text-gray-900" },
  youtube: { label: "YouTube", Icon: YouTubeIcon, brandClass: "text-red-600" },
  generic: { label: "Link", Icon: LinkIcon, brandClass: "text-gray-500" },
};

const VIDEO_TYPES = new Set(["video", "reel"]);

function hostFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export const ResourceEmbedCard: React.FC<ResourceEmbedCardProps> = ({
  resource,
  variant = "portal",
  onRemove,
  isRemoving = false,
}) => {
  const embed = resource.embed;

  // Highest priority across all variants except dense: locally-hosted
  // media imported via Apify. Real inline video / carousel, no Meta
  // widget involvement.
  const localItems = playableLocalMedia(resource);
  if (localItems.length > 0 && variant !== "dense") {
    return (
      <div className="space-y-2">
        <InlineMediaPlayer items={localItems} />
        {(resource.note || embed?.title) && (
          <p className="text-xs text-gray-500 px-1 truncate">
            {resource.note || embed?.title}
          </p>
        )}
      </div>
    );
  }

  if (!embed || embed.status === "failed") {
    return (
      <ResourcePill
        url={resource.url}
        note={resource.note}
        onRemove={onRemove}
        isRemoving={isRemoving}
      />
    );
  }

  if (variant === "dense") {
    return (
      <DenseCard
        resource={resource}
        embed={embed}
        onRemove={onRemove}
        isRemoving={isRemoving}
      />
    );
  }

  return (
    <FullCard
      resource={resource}
      embed={embed}
      onRemove={variant === "admin" ? onRemove : undefined}
      isRemoving={isRemoving}
    />
  );
};

interface CardBodyProps {
  resource: IdeaResource;
  embed: IdeaResourceEmbed;
  onRemove?: () => void;
  isRemoving?: boolean;
}

const FullCard: React.FC<CardBodyProps> = ({
  resource,
  embed,
  onRemove,
  isRemoving = false,
}) => {
  const provider = PROVIDER_BADGES[embed.provider] ?? PROVIDER_BADGES.generic;
  const isVideo = VIDEO_TYPES.has(embed.embedType);
  const isRich = embed.status === "ok";

  return (
    <a
      href={resource.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group/embed flex items-stretch gap-3 bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 hover:shadow-sm transition"
    >
      <Thumbnail
        url={embed.thumbnailUrl}
        title={embed.title}
        showPlay={isVideo}
      />
      <div className="flex-1 min-w-0 p-3 flex flex-col gap-1">
        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide">
          {isRich ? (
            <span className={`inline-flex items-center gap-1 ${provider.brandClass}`}>
              <provider.Icon className="w-3.5 h-3.5" />
              {provider.label}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-gray-500">
              <LinkIcon className="w-3.5 h-3.5" />
              {hostFromUrl(resource.url)}
            </span>
          )}
          {resource.note && (
            <span className="text-gray-400 normal-case font-normal tracking-normal truncate">
              · {resource.note}
            </span>
          )}
        </div>
        <div className="text-sm font-medium text-gray-900 line-clamp-2">
          {embed.title || hostFromUrl(resource.url)}
        </div>
        {embed.authorName && (
          <div className="text-xs text-gray-500 truncate">
            {embed.authorName}
          </div>
        )}
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          disabled={isRemoving}
          aria-label="Remove resource"
          className="self-start mt-2 mr-2 p-1 text-gray-400 hover:text-rose-600 opacity-0 group-hover/embed:opacity-100 transition-opacity shrink-0"
        >
          <CloseIcon className="w-4 h-4" />
        </button>
      )}
    </a>
  );
};

const DenseCard: React.FC<{
  resource: IdeaResource;
  embed: IdeaResourceEmbed;
  onRemove?: () => void;
  isRemoving?: boolean;
}> = ({ resource, embed, onRemove, isRemoving = false }) => {
  const provider = PROVIDER_BADGES[embed.provider] ?? PROVIDER_BADGES.generic;
  return (
    <div className="group/embed-dense inline-flex items-center gap-2 max-w-full px-2 py-1 text-xs text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition">
      <a
        href={resource.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 min-w-0"
      >
        <provider.Icon className={`w-3.5 h-3.5 shrink-0 ${provider.brandClass}`} />
        <span className="truncate max-w-[160px]">
          {embed.title || resource.note || hostFromUrl(resource.url)}
        </span>
      </a>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          disabled={isRemoving}
          aria-label="Remove resource"
          className="p-0.5 text-gray-400 hover:text-rose-600 opacity-0 group-hover/embed-dense:opacity-100 transition-opacity shrink-0"
        >
          <CloseIcon className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

const Thumbnail: React.FC<{
  url?: string | null;
  title?: string | null;
  showPlay: boolean;
}> = ({ url, title, showPlay }) => {
  const [errored, setErrored] = useState(false);
  const hasImage = !!url && !errored;

  // Locked-aspect square frame with self-center so the thumbnail doesn't
  // stretch vertically to match a tall text panel. object-cover crops the
  // source to a square (Instagram/Pinterest convention for previews).
  return (
    <div className="relative w-24 h-24 sm:w-32 sm:h-32 shrink-0 self-center bg-gray-100 flex items-center justify-center overflow-hidden">
      {hasImage ? (
        <img
          src={url!}
          alt={title || ""}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer-when-downgrade"
          onError={() => setErrored(true)}
          className="w-full h-full object-cover"
        />
      ) : (
        <ImageIcon className="w-6 h-6 text-gray-400" />
      )}
      {showPlay && hasImage && (
        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="w-9 h-9 rounded-full bg-black/50 flex items-center justify-center">
            <PlayIcon className="w-4 h-4 text-white" />
          </span>
        </span>
      )}
    </div>
  );
};

function playableLocalMedia(r: IdeaResource): IdeaResourceLocalMediaItem[] {
  const items = r.embed?.localMedia;
  if (!Array.isArray(items)) return [];
  return items.filter(
    (item) => item && typeof item.url === "string" && item.url.length > 0
  ) as IdeaResourceLocalMediaItem[];
}
